import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ISSUE_LABEL, RANGE_LABEL, type PodSummary } from "@k8s-dashboard/contracts";
import { drillKeys, useScope, useWorkloadDetail } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { Panel, StatTile, StatusBadge } from "@/components/primitives";
import { SectionView } from "@/components/SectionState";
import { VirtualTable } from "@/components/VirtualTable";
import { Breadcrumb, LogsLink, OwnerChain, TrendsGrid, UsageBar, drillCrumbs, refPath } from "@/components/drill";
import { EventFeed } from "@/features/overview/panels";
import { duration, num, since } from "@/lib/format";
import { PageError, PageHeader, useDrillControls, useInvalidate } from "./common";
import { SloCard } from "./SloCard";
import { RolloutCompareCard } from "./RolloutCompareCard";
import { MetricInvestigationCard } from "./MetricInvestigationCard";
import { AgentEntry } from "@/features/agent/AgentEntry";
import { WorkloadStructureCard } from "./WorkloadStructureCard";
import { LiveLogs } from "./LiveLogs";
import { useApplyNamespaceSelection } from "@/state/namespaceSelectionIdentity";
import { useT } from "@/i18n/I18nProvider";

const ROLLOUT_LABEL = {
  Complete: "완료",
  Progressing: "진행 중",
  Stalled: "지연",
  Paused: "일시정지",
} as const;

/**
 * Workload 상세 — 이슈 #15
 * desired/available/updated replica, rollout 상태, OwnerReference 체인, Pod 목록.
 */
export function WorkloadDetail() {
  const { search } = useLocation();
  /* namespace·kind·name 셋 다 경로가 정합니다. 쿼리에서 읽지 않습니다. (#111) */
  const { namespace = "", kind = "Deployment", name = "" } = useParams();
  const ns = namespace;
  const { clusterId, range, from, to, refreshMs } = useDashboardParams();
  const scope = useScope();
  const applyNamespaceSelection = useApplyNamespaceSelection();
  const { t } = useT("workload");
  /* SLO 목표·기간 선택은 URL에 둡니다 — 자동 갱신이 사용자의 선택을 지우지 않고,
     북마크한 주소가 같은 화면을 다시 엽니다. 값은 서버가 준 적용 가능 목록에서만
     나오고, 목록 밖 값은 서버가 닫습니다(ADR 0051 D1a). */
  const urlParams = new URLSearchParams(search);
  const objective = urlParams.get("objective") ?? undefined;
  const sloWindow = urlParams.get("sloWindow") ?? undefined;
  /* 비교 선택자는 URL에 둡니다 — 자동 갱신이 선택을 지우지 않고 북마크가 같은
     화면을 엽니다. `compareAnchor`는 **서버가 발급한 anchorId**입니다. */
  const compareAnchor = urlParams.get("compareAnchor") ?? undefined;
  const compareWindow = urlParams.get("compareWindow") ?? undefined;
  /* 조사 요청도 URL에 둡니다(ADR 0055 D2) — 요청 여부만 고르며 서버가 값을 검증합니다. */
  const investigation = urlParams.get("investigation") ?? undefined;
  /* 요청한 워크로드 UID입니다(#208 — **선택**).
     없으면 지금까지와 똑같이 이름으로 찾습니다. 있으면 서버가 캐시 재사용 **전에**
     현재 informer UID와 대조하고, 어긋나면 404로 답합니다 — 같은 이름의 새 세대를
     옛 링크로 열어 주지 않기 위해서입니다. 이 값은 매처가 되지 않습니다.
     Warning Events 화면만 이 값을 싣습니다. 공용 링크 helper는 싣지 않습니다 —
     기존 Overview·Topology·Alerts 링크까지 UID 묶음 요청이 되면 central 모드의
     기존 탐색이 깨집니다. */
  const uid = urlParams.get("uid") ?? undefined;
  const q = useWorkloadDetail(clusterId, ns, kind, name, range, refreshMs, from, to, objective, sloWindow, compareAnchor, compareWindow, investigation, uid);
  const invalidate = useInvalidate(
    drillKeys.workload(clusterId, ns, kind, name, range, from, to, objective, sloWindow, compareAnchor, compareWindow, investigation, uid),
  );
  const { controls } = useDrillControls(invalidate, q.isFetching, q.dataUpdatedAt || undefined);

  const w = q.data?.workload.data;
  const ref = q.data?.generatedAt;
  const workloadIdentity = [clusterId, ns, kind, name, w?.ref.workloadUid ?? "", q.data?.clusterId ?? "", q.data?.namespace ?? ""].join("\u0000");
  const [liveSelection, setLiveSelection] = useState<{ ownerIdentity: string; pod: PodSummary }>();
  /* render 단계에서도 identity를 대조합니다. effect 한 프레임을 기다리는 동안 옛 Pod를
     새 Scope/Workload의 대상으로 다시 라벨링하지 않습니다. */
  const livePod = liveSelection?.ownerIdentity === workloadIdentity ? liveSelection.pod : undefined;
  const candidateKey = q.data?.pods.data?.map((pod) => pod.uid).join("\u0000") ?? "";
  const manageableKind = w?.kind === "Deployment" || w?.kind === "StatefulSet" || w?.kind === "DaemonSet";
  const managePath = w
    ? `/namespaces/${encodeURIComponent(w.namespace)}/workloads/${encodeURIComponent(w.kind)}/${encodeURIComponent(w.name)}/manage`
    : "";

  /* 응답의 workload UID에 묶인 후보만 유지합니다. Scope/workload/candidate가 바뀌면
     선택을 즉시 버리고, 아래의 단일 LiveLogs owner가 이전 stream을 정리합니다. */
  useEffect(() => {
    setLiveSelection((selected) => {
      if (!q.data || !w || selected?.ownerIdentity !== workloadIdentity) return undefined;
      return q.data.pods.data?.some((pod) => pod.uid === selected.pod.uid) ? selected : undefined;
    });
  }, [workloadIdentity, candidateKey, q.data, w]);

  return (
    <div className="page">
      <PageHeader
        crumbs={
          <Breadcrumb
            items={drillCrumbs(search, ns, { label: name })}
          />
        }
        title={
          <span className="row">
            {name}
            {w && <StatusBadge severity={w.severity} small />}
          </span>
        }
        subtitle={`${kind} · ${ns} · ${clusterId} · ${RANGE_LABEL[range]}`}
        controls={controls}
        actions={
          w ? (
            /* AI Agent(ADR 0026)는 URL의 이름이 아니라 **응답의 Workload UID**로 대상을
               지목합니다. 이름은 표시용으로만 넘깁니다. */
            <span className="row row--wrap">
              {scope.data?.canManageWorkloads && manageableKind && (
                <Link
                  className="btn"
                  to={managePath}
                  onClick={(event) => {
                    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                    if (!applyNamespaceSelection({ mode: "selected", names: [w.namespace] })) event.preventDefault();
                  }}
                >
                  {t("workload.manage.open")}
                </Link>
              )}
              <AgentEntry
                clusterId={clusterId}
                namespace={w.namespace}
                range={range}
                workloadUid={w.ref.workloadUid}
                subjectName={w.name}
                subjectKind={w.kind}
              />
            </span>
          ) : undefined
        }
      />

      {q.isError ? (
        <PageError error={q.error} onRetry={invalidate} />
      ) : (
        <>
          {w && (
            <div className="grid grid--kpi">
              <StatTile
                label="Replica (ready/desired)"
                value={`${w.replicas.ready}/${w.replicas.desired}`}
                tone={w.replicas.ready < w.replicas.desired ? "warning" : undefined}
                footnote={`available ${w.replicas.available} · updated ${w.replicas.updated}`}
              />
              <StatTile
                label="Rollout"
                value={ROLLOUT_LABEL[w.rollout.status]}
                tone={w.rollout.status === "Stalled" ? "degraded" : undefined}
                footnote={w.rollout.message ?? "최신 세대가 모두 배포됨"}
              />
              <StatTile
                label="재시작"
                value={num(w.restarts)}
                delta={w.restarts > 0 ? { text: "↑", kind: "bad" } : { text: "→", kind: "flat" }}
                footnote={`${RANGE_LABEL[range]} 합계`}
              />
              <StatTile
                label="CPU / Request"
                value={`${(w.usage.cpuVsRequest * 100).toFixed(0)}%`}
                footnote={`${num(w.usage.cpuMilli)}m / ${num(w.usage.cpuRequestMilli)}m`}
              />
              <StatTile
                label="Memory / Request"
                value={`${(w.usage.memoryVsRequest * 100).toFixed(0)}%`}
                footnote={`${num(w.usage.memoryMib)}MiB / ${num(w.usage.memoryRequestMib)}MiB`}
              />
            </div>
          )}

          <div className="grid grid--split">
            <Panel
              title="Pod"
              subtitle="이름을 클릭하면 Pod 상세로 이동합니다"
              section={q.data?.pods}
              referenceIso={ref}
              flush
            >
              <SectionView section={q.data?.pods} loading={q.isLoading} emptyTitle="Pod가 없습니다">
                {(pods) => <PodTable pods={pods} search={search} referenceIso={ref!} onLive={(pod) => setLiveSelection({ ownerIdentity: workloadIdentity, pod })} />}
              </SectionView>
            </Panel>

            <div className="grid">
              <Panel
                title="OwnerReference"
                subtitle="Deployment → ReplicaSet → Pod"
                section={q.data?.ownerChain}
                referenceIso={ref}
              >
                <SectionView
                  section={q.data?.ownerChain}
                  loading={q.isLoading}
                  emptyTitle="중간 소유자가 없습니다"
                  emptyDetail={`${kind}은(는) Pod를 직접 소유합니다.`}
                >
                  {(chain) => <OwnerChain chain={chain} />}
                </SectionView>
              </Panel>

              {w && (
                <Panel title="배포 정보" section={q.data?.workload} referenceIso={ref}>
                  <div className="facts">
                    <div className="fact">
                      <span className="fact__label">이미지</span>
                      <span className="fact__value ds-ident">{w.images.join(", ")}</span>
                    </div>
                    <div className="fact">
                      <span className="fact__label">Workload UID</span>
                      <span className="fact__value ds-ident">{w.ref.workloadUid}</span>
                    </div>
                    <div className="fact">
                      <span className="fact__label">생성 후</span>
                      <span className="fact__value num">{duration(w.ageSeconds)}</span>
                    </div>
                    <div className="fact">
                      <span className="fact__label">문제</span>
                      <span className="fact__value">
                        {w.issues.length ? w.issues.map((i) => ISSUE_LABEL[i]).join(" · ") : "없음"}
                      </span>
                    </div>
                  </div>
                </Panel>
              )}
            </div>
          </div>

          {/* HTTP 요청 SLO · Error Budget (ADR 0051 · #157). **조회 전용**이고 같은
              응답의 slo 섹션만 읽습니다 — 새 요청·훅·폴링을 만들지 않습니다. */}
          <WorkloadStructureCard section={q.data?.structured} referenceIso={ref} />
          <SloCard section={q.data?.slo} referenceIso={ref} />
          <RolloutCompareCard section={q.data?.rolloutCompare} referenceIso={ref} />
          <MetricInvestigationCard section={q.data?.metricInvestigation} referenceIso={ref} />

          <SectionView section={q.data?.trends} loading={q.isLoading} emptyTitle="추세 데이터가 없습니다">
            {(panels) => (
              <TrendsGrid panels={panels} section={q.data?.trends} range={range} referenceIso={ref} labelPrefix={name} />
            )}
          </SectionView>

          <Panel title="최근 Event" section={q.data?.events} referenceIso={ref} flush>
            <SectionView section={q.data?.events} loading={q.isLoading} emptyTitle="관련 이벤트가 없습니다">
              {(events) => <EventFeed events={events} referenceIso={ref!} />}
            </SectionView>
          </Panel>
        </>
      )}

      {/* stream controller owner는 loading/error/no-target에서도 같은 인스턴스입니다.
          대상만 비우므로 늦게 settle하는 abort와 새 owner가 겹치지 않습니다. */}
      <LiveLogs
        clusterId={clusterId}
        namespace={livePod?.namespace ?? ""}
        podName={livePod?.name ?? ""}
        podUid={livePod?.uid ?? ""}
        ownerIdentity={workloadIdentity}
        search={search}
        autoOpen={Boolean(livePod)}
      />
    </div>
  );
}

const COLS = ["30%", "10%", "8%", "8%", "16%", "14%", "8%", "6%"];

function PodTable({ pods, search, referenceIso, onLive }: { pods: PodSummary[]; search: string; referenceIso: string; onLive: (pod: PodSummary) => void }) {
  return (
    <VirtualTable
      items={pods}
      rowHeight={34}
      height={380}
      /* key는 이름이 아니라 UID입니다. 재생성된 동명 Pod가 같은 행으로 합쳐지면 안 됩니다. */
      getKey={(p) => p.uid}
      header={
        <tr>
          <th style={{ width: COLS[0] }}>Pod</th>
          <th style={{ width: COLS[1] }}>Phase</th>
          <th style={{ width: COLS[2] }}>Ready</th>
          <th className="ds-num" style={{ width: COLS[3] }}>
            재시작
          </th>
          <th style={{ width: COLS[4] }}>CPU / Request</th>
          <th style={{ width: COLS[5] }}>Node</th>
          <th style={{ width: COLS[6] }}>시작</th>
          <th style={{ width: COLS[7] }}>로그</th>
        </tr>
      }
      renderRow={(p) => (
        <>
          <td className="ds-ident" style={{ width: COLS[0] }}>
            <Link to={refPath({ podName: p.name, namespace: p.namespace, podUid: p.uid }, search)}>
              {p.name}
            </Link>
            {p.finishedAt && <span className="muted"> · 종료된 인스턴스</span>}
          </td>
          <td style={{ width: COLS[1] }}>
            <StatusBadge severity={p.severity} label={p.phase} small />
          </td>
          <td style={{ width: COLS[2] }}>{p.ready}</td>
          <td className="ds-num" style={{ width: COLS[3] }}>
            {p.restarts}
          </td>
          <td style={{ width: COLS[4] }}>
            <UsageBar ratio={p.usage.cpuVsRequest} label="CPU Request" />
          </td>
          <td className="ds-ident" style={{ width: COLS[5] }}>
            {p.node}
          </td>
          <td className="muted num" style={{ width: COLS[6] }}>
            {since(p.startedAt, referenceIso)}
          </td>
          <td style={{ width: COLS[7] }}>
            <span className="row row--wrap">
              <LogsLink resourceRef={p.ref} search={search}>저장 로그</LogsLink>
              <button type="button" className="linkish" onClick={() => onLive(p)}>Live Logs</button>
            </span>
          </td>
        </>
      )}
    />
  );
}
